import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { ProjectSummary } from '@/types'

import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { SectionLabel } from '@/components/generals/section-label'
import WorkHero from '@/components/sections/work-hero'
import ProjectGrid from '@/components/sections/project-grid'
import FinalCta from '@/components/sections/final-cta'

import { REVALIDATE_SECONDS, getProjects } from '@/lib/content'

interface WorkPageProps {
    projects: ProjectSummary[]
}

const WorkPage: FC<WorkPageProps> = ({ projects }): ReactNode => {
    const [filter, setFilter] = useState('All')

    const featured = projects.find((project) => project.featured) ?? projects[0]
    const categories = ['All', ...Array.from(new Set(projects.map((project) => project.category).filter(Boolean)))]
    const industries = Array.from(new Set(projects.map((project) => project.industry).filter(Boolean)))
    const visible = filter === 'All' ? projects : projects.filter((project) => project.category === filter)

    return (
        <Layout>
            <Seo title='Our work' description='Selected campaigns, brand identities and performance marketing projects by BLITZ, the creative marketing agency.' />

            <WorkHero projects={projects} />

            {featured && (
                <section aria-labelledby='featured-title' data-nav-theme='light' className='bg-cream text-charcoal'>
                    <div className='mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28'>
                        <SectionLabel>Featured campaign</SectionLabel>
                        <Link
                            href={`/work/${featured.slug}`}
                            className='group mt-10 grid gap-10 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-coral lg:grid-cols-12 lg:items-center lg:gap-12'
                        >
                            <div className='relative lg:col-span-7'>
                                <div aria-hidden='true' className='absolute -bottom-4 -left-4 h-2/3 w-1/2 md:-bottom-6 md:-left-6' style={{ backgroundColor: featured.accent }} />
                                <div data-reveal-image className='relative aspect-[16/11] overflow-hidden bg-stone'>
                                    {featured.cover && (
                                        <Image src={featured.cover} alt={featured.coverAlt} fill sizes='(min-width: 1024px) 58vw, 100vw' className='object-cover transition-[scale] duration-[1400ms] ease-[var(--ease-out-expo)] group-hover:scale-[1.04]' />
                                    )}
                                </div>
                            </div>
                            <div data-reveal className='lg:col-span-5'>
                                <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-charcoal/80'>
                                    {[featured.industry, featured.year].filter(Boolean).join(' / ')}
                                </p>
                                <h2 id='featured-title' className='mt-4 font-heading font-wide text-[clamp(2.5rem,4.6vw,4.25rem)] font-black uppercase leading-[0.88] tracking-[-0.045em]'>
                                    {featured.name}
                                </h2>
                                <p className='mt-5 font-heading text-xl font-bold leading-snug tracking-[-0.02em] md:text-2xl'>{featured.tagline}</p>
                                <p className='mt-4 max-w-md text-[16px] leading-relaxed text-charcoal/85'>{featured.summary}</p>
                                {featured.services.length > 0 && (
                                    <p className='mt-6 text-[14px] text-charcoal/85'>
                                        <span className='font-mono text-[10px] uppercase tracking-[0.18em] text-charcoal/75'>Services </span>
                                        {featured.services.map((service) => service.title).join(', ')}
                                    </p>
                                )}
                                <span className='mt-8 inline-flex items-center gap-2 border-b border-charcoal pb-1 text-[13px] font-semibold uppercase tracking-[0.08em]'>
                                    View case study
                                    <ArrowUpRight aria-hidden='true' className='size-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                                </span>
                            </div>
                        </Link>
                    </div>
                </section>
            )}

            <section id='all-projects' aria-labelledby='all-projects-title' data-nav-theme='light' className='bg-stone text-charcoal'>
                <div className='mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28'>
                    <div className='grid gap-10 lg:grid-cols-12 lg:items-end'>
                        <div className='lg:col-span-6'>
                            <SectionLabel>All projects</SectionLabel>
                            <h2 id='all-projects-title' className='mt-6 font-heading type-section'>
                                Browse by discipline.
                            </h2>
                        </div>

                        <div className='space-y-6 lg:col-span-6'>
                            <div role='group' aria-label='Filter projects by category' className='flex flex-wrap gap-2 lg:justify-end'>
                                {categories.map((category) => (
                                    <button
                                        key={category}
                                        type='button'
                                        aria-pressed={filter === category}
                                        onClick={() => setFilter(category)}
                                        className={cn(
                                            'h-10 rounded-sm border px-4 text-[12px] font-semibold uppercase tracking-[0.12em] transition-colors duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-charcoal',
                                            filter === category ? 'border-charcoal bg-charcoal text-cream' : 'border-charcoal/35 text-charcoal hover:border-charcoal'
                                        )}
                                    >
                                        {category}
                                    </button>
                                ))}
                            </div>
                            {industries.length > 0 && (
                                <p className='text-[14px] text-charcoal/85 lg:text-right'>
                                    <span className='font-mono text-[10px] uppercase tracking-[0.18em] text-charcoal/75'>Industries </span>
                                    {industries.join(', ')}
                                </p>
                            )}
                        </div>
                    </div>

                    <p aria-live='polite' className='sr-only'>
                        {visible.length} {visible.length === 1 ? 'project' : 'projects'} shown
                    </p>

                    {visible.length > 0 ? (
                        <ProjectGrid key={filter} projects={visible} tone='light' className='mt-14 md:mt-16' />
                    ) : (
                        <p className='mt-14 text-charcoal/80'>No projects in this category yet.</p>
                    )}
                </div>
            </section>

            <FinalCta />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<WorkPageProps> = async () => {
    const projects = await getProjects()

    return {
        props: { projects },
        revalidate: REVALIDATE_SECONDS
    }
}

export default WorkPage
